import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useQueryClient } from '@tanstack/react-query';

import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { GoogleAddressAutocomplete } from '@/components/ui/GoogleAddressAutocomplete';
import { createPatient } from '@/api/patients';

// =============================================================================
// VALIDATION SCHEMA
// =============================================================================

const patientSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(100),
  last_name: z.string().min(1, 'Last name is required').max(100),
  date_of_birth: z.string().optional().refine(
    (val) => {
      if (!val) return true;
      const dob = new Date(val);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear() - 
        ((today.getMonth() < dob.getMonth() || 
          (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) ? 1 : 0);
      return age >= 18;
    },
    { message: 'Patient must be 18 years or older' }
  ),
  gender: z.enum(['male', 'female']).optional(),
  email: z.string().email('Invalid email format').optional().or(z.literal('')),
  phone_country: z.string().default('+1'),
  phone_primary: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const digits = val.replace(/\D/g, '');
        return digits.length === 10;
      },
      { message: 'Phone must be 10 digits' }
    ),
  address_line1: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().optional(),
});

type PatientFormData = z.infer<typeof patientSchema>;

// =============================================================================
// COMPONENT
// =============================================================================

const AddPatient: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: undefined,
      email: '',
      phone_country: '+1',
      phone_primary: '',
      address_line1: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
    },
  });

  // Watch address field for manual changes
  const addressLine1Value = watch('address_line1') || '';

  // Handle place selection from Google autocomplete
  const handlePlaceSelected = (place: {
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  }) => {
    // Auto-fill the address fields
    setValue('address_line1', place.address);
    setValue('city', place.city);
    setValue('state', place.state);
    setValue('postal_code', place.postalCode);
    setValue('country', place.country);
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const onSubmit = async (data: PatientFormData) => {
    setSubmitting(true);
    try {
      // Format phone number - keep only digits
      const phoneValue = data.phone_primary?.replace(/\D/g, '');

      const payload: any = {
        first_name: data.first_name,
        last_name: data.last_name,
      };

      // Add optional fields only if provided
      if (data.date_of_birth) payload.date_of_birth = data.date_of_birth;
      if (data.gender) payload.gender = data.gender;
      if (data.email) payload.email = data.email;
      if (phoneValue) {
        payload.phone_primary = phoneValue;
        // Remove '+' sign from mobile code for backend storage
        payload.mobile_code = (data.phone_country || '+1').replace('+', '');
      }
      if (data.address_line1) payload.address_line1 = data.address_line1;
      if (data.city) payload.city = data.city;
      if (data.state) payload.state = data.state;
      if (data.postal_code) payload.postal_code = data.postal_code;
      if (data.country) payload.country = data.country;

      await createPatient(payload);
      toast.success('Patient added successfully');
      // Invalidate patients query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ['patients'] });
      navigate('/patients');
    } catch (err: any) {
      // Handle duplicate email error specifically
      if (err?.response?.status === 409) {
        const errorMsg = err?.response?.data?.detail || 'A patient with this email already exists.';
        toast.error(errorMsg);
      } else {
        const errorMsg = err?.response?.data?.detail || 'Failed to add patient';
        toast.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add New Patient</h1>
          <p className="text-gray-600">Enter patient information</p>
        </div>
      </div>

      {/* Form */}
      <Card className="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                {...register('first_name')}
                error={errors.first_name?.message}
                required
              />
              <Input
                label="Last Name"
                {...register('last_name')}
                error={errors.last_name?.message}
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <Controller
                  name="date_of_birth"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      selected={field.value ? new Date(field.value) : null}
                      onChange={(date: Date | null) => {
                        field.onChange(date ? date.toISOString().split('T')[0] : '');
                      }}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="Select date of birth"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  )}
                />
                {errors.date_of_birth && (
                  <p className="mt-1 text-sm text-red-600">{errors.date_of_birth.message}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Must be 18 years or older
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  {...register('gender')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
                {errors.gender && (
                  <p className="mt-1 text-sm text-red-600">{errors.gender.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Contact Information
            </h2>
            <div className="space-y-4">
              <Input
                label="Email"
                type="email"
                {...register('email')}
                error={errors.email?.message}
                placeholder="patient@example.com"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <select
                    {...register('phone_country')}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+1">🇨🇦 +1</option>
                  </select>
                  <Controller
                    name="phone_primary"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        value={field.value || ''}
                        type="tel"
                        placeholder="(555) 123-4567"
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          field.onChange(formatted);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    )}
                  />
                </div>
                {errors.phone_primary && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.phone_primary.message}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Enter 10-digit phone number
                </p>
              </div>
            </div>
          </div>

          {/* Address (Optional) */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Address <span className="text-sm font-normal text-gray-500">(Optional)</span>
            </h2>
            <div className="space-y-4">
              <GoogleAddressAutocomplete
                label="Street Address"
                value={addressLine1Value}
                onChange={(value) => setValue('address_line1', value)}
                onPlaceSelected={handlePlaceSelected}
                error={errors.address_line1?.message}
                placeholder="Start typing to search address..."
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="City"
                  {...register('city')}
                  error={errors.city?.message}
                />
                <Input
                  label="State"
                  {...register('state')}
                  error={errors.state?.message}
                  placeholder="CA"
                />
                <Input
                  label="Zip Code"
                  {...register('postal_code')}
                  error={errors.postal_code?.message}
                  placeholder="12345"
                />
              </div>
              <Input
                label="Country"
                {...register('country')}
                error={errors.country?.message}
                placeholder="Auto-filled from address"
                readOnly
                className="bg-gray-50"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/patients')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={submitting}>
              Add Patient
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddPatient;
